import React, { useCallback } from "react";
import { useAppSelector } from "../../../redux/configureStore";
import { ConfirmationRequest, VenueSessionStatus } from "../../../app/types";
import { useUpdateSessionStateMutation } from "../../api/api";
import {
  useGetSessionsQuery,
  useJoinData,
  useSession,
} from "../../api/helpers";
import GradientButton from "../../common/GradientButton";
import useVenueId from "../../common/useVenueId";
import useConfirmation from "../../modals/useConfirmation";
import { getComposedLayout, getPresenters } from "../selectors";

interface Props {
  onEndSession: () => void;
}

const startRecordingConfirmationRequest: ConfirmationRequest = {
  title: "Start recorded session?",
  msg: "This session will be recorded.",
  acceptText: "Continue",
};

const endSessionConfirmationRequest: ConfirmationRequest = {
  title: "End Session?",
  msg: "Ending the session will disconnect all viewers.",
  acceptText: "End Session",
};

const StartSessionButton = ({ onEndSession }: Props) => {
  const venueId = useVenueId();
  const session = useSession();
  const joinData = useJoinData();
  const presenters = useAppSelector(getPresenters(session?.sessionId));
  const layout = useAppSelector(getComposedLayout(session?.sessionId));

  const [updateSessionStatus, { isLoading: isUpdatingSession }] =
    useUpdateSessionStateMutation();

  const updateSession = useCallback(
    (status: VenueSessionStatus) => {
      if (session?.sessionId) {
        updateSessionStatus({
          sessionId: session.sessionId,
          status,
          venueId,
          layout,
        });
      }
    },
    [layout, session?.sessionId, updateSessionStatus, venueId],
  );

  const showStartRecordingConfirmation = useConfirmation(
    startRecordingConfirmationRequest,
    useCallback(
      (userAccepted) => {
        if (userAccepted) {
          updateSession("InProgress");
        }
      },
      [updateSession],
    ),
  );

  const showEndSessionConfirmation = useConfirmation(
    endSessionConfirmationRequest,
    useCallback(
      (userAccepted) => {
        if (userAccepted) {
          updateSession("Finished");
          onEndSession();
        }
      },
      [onEndSession, updateSession],
    ),
  );

  const { isLoading: isSessionLoading } = useGetSessionsQuery();

  const onClick = useCallback(() => {
    const status = session?.status === "InProgress" ? "Finished" : "InProgress";
    if (status === "InProgress" && joinData?.isRecorded === true) {
      showStartRecordingConfirmation();
    } else if (status === "Finished") {
      showEndSessionConfirmation();
    } else {
      updateSession(status);
    }
  }, [
    joinData?.isRecorded,
    session?.status,
    showEndSessionConfirmation,
    showStartRecordingConfirmation,
    updateSession,
  ]);

  if (session === undefined) {
    return null;
  }

  return (
    <GradientButton
      isLoading={isUpdatingSession || isSessionLoading}
      onClick={onClick}
      disabled={presenters.length < 1 && session?.status !== "InProgress"}
    >
      {session.status === "InProgress" ? "End Session" : "Start Session"}
    </GradientButton>
  );
};

export default StartSessionButton;
